import type { ChatMessage } from '../types/chat.types';
import { authFetch } from '@api/authFetch';
import { API_BASE_URL } from '@config/constants';

/** Parse FastAPI-style JSON error bodies (`detail` string or validation list). */
async function parseHttpError(response: Response): Promise<string> {
  const text = await response.text();
  try {
    const j = JSON.parse(text) as { detail?: unknown };
    if (typeof j.detail === 'string') return j.detail;
    if (Array.isArray(j.detail)) {
      return j.detail
        .map((d: { msg?: string }) => (typeof d?.msg === 'string' ? d.msg : JSON.stringify(d)))
        .join('; ');
    }
    if (j.detail != null) return String(j.detail);
  } catch {
    /* use raw text */
  }
  const trimmed = text.trim();
  return trimmed || `Chat request failed (${response.status})`;
}

/**
 * Optional per-stream hooks. When ``category`` is supplied, the API
 * emits curated ``{"type":"log","text":...}`` SSE events at known
 * stage boundaries inside the agent thread; the wizard's loading
 * bubble renders these as a rolling 4-line progress display. Pure
 * chat consumers (the floating panel) leave both undefined.
 */
export interface ChatStreamOptions {
  onLog?: (text: string) => void;
  category?: string;
}

async function consumeChatSse(
  content: string,
  onToken: (chunk: string) => void,
  opts?: ChatStreamOptions,
): Promise<'ok' | 'empty'> {
  const url = `${API_BASE_URL}/chat/stream`;
  const response = await authFetch(url, {
    method: 'POST',
    body: JSON.stringify({ message: content, category: opts?.category }),
  });
  if (!response.ok) {
    throw new Error(await parseHttpError(response));
  }
  if (!response.body) {
    throw new Error('No response body');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let got = false;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split('\n\n');
    buf = parts.pop() ?? '';
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith('data:')) continue;
      const raw = line.slice(5).trim();
      try {
        const evt = JSON.parse(raw) as { type?: string; text?: string; message?: string };
        if (evt.type === 'log' && typeof evt.text === 'string' && opts?.onLog) {
          opts.onLog(evt.text);
        }
        if (evt.type === 'token' && typeof evt.text === 'string') {
          got = true;
          onToken(evt.text);
        }
        if (evt.type === 'error') {
          throw new Error(typeof evt.message === 'string' ? evt.message : 'Chat stream error');
        }
      } catch (e) {
        if (e instanceof SyntaxError) continue;
        throw e;
      }
    }
  }
  return got ? 'ok' : 'empty';
}

export const chatService = {
  /**
   * Streams assistant tokens from ``POST /api/v1/chat/stream`` when RAG is enabled on the API.
   * Returns false if the stream produced no text (caller should show a non-mock notice).
   *
   * ``opts.onLog`` and ``opts.category`` are optional. When supplied,
   * the API interleaves stage-progress messages (``type: "log"``) with
   * the existing ``token`` chunks; the wizard renders these as a
   * rolling progress display in the agent loading bubble. Callers
   * that don't care leave them undefined and behaviour is unchanged.
   */
  async streamAssistantReply(
    content: string,
    onToken: (chunk: string) => void,
    opts?: ChatStreamOptions,
  ): Promise<boolean> {
    const outcome = await consumeChatSse(content, onToken, opts);
    return outcome === 'ok';
  },

  /**
   * One-shot reply (floating panel): same stream as full chat; errors become agent message text.
   */
  async sendMessage(content: string): Promise<ChatMessage> {
    let assembled = '';
    try {
      const ok = await consumeChatSse(content, (t) => {
        assembled += t;
      });
      if (ok && assembled.trim()) {
        return {
          id: `msg-${Date.now()}`,
          role: 'agent',
          content: assembled,
          timestamp: Date.now(),
        };
      }
      return {
        id: `msg-${Date.now()}`,
        role: 'agent',
        content:
          'No response was received from the assistant. Check that RAG is enabled and configured on the server.',
        timestamp: Date.now(),
      };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      return {
        id: `msg-${Date.now()}`,
        role: 'agent',
        content: msg,
        timestamp: Date.now(),
      };
    }
  },
};
